import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { catchError, map, Observable, of, switchMap, take } from 'rxjs';

import { AuthDataService } from '@common/service/auth-data.service';

@Injectable({
  providedIn: 'root',
})
export class AuthCacheGuardService {
  constructor(
    private authDataService: AuthDataService,
    private http: HttpClient,
  ) {}

  /**
   * Fetches the live Cognito well-known discovery document and compares its
   * `token_endpoint` hostname against the cached `tokenEndpoint` in the OIDC
   * localStorage blob (`0-{clientId}`).
   *
   * If the hostnames differ (e.g. after a Cognito hosted-UI domain cutover
   * from oauth.kflowdev.com to oauth.aiworkbook.dev), the cached blob is
   * removed so angular-auth-oidc-client performs a fresh discovery.
   *
   * Skips the HTTP call entirely when nothing is cached.
   * Fails open on any error — never blocks app startup.
   *
   * Call this BEFORE oidcSecurityService.forceRefreshSession() / checkAuth().
   */
  invalidateIfStale(): Observable<void> {
    return this.authDataService.getAuthData().pipe(
      take(1),
      switchMap((oidcConfig) => {
        const storageKey = `0-${oidcConfig.client_id}`;
        const cachedEndpoints = this.readCachedEndpoints(storageKey);

        if (!cachedEndpoints?.tokenEndpoint) {
          // Nothing cached yet — skip the HTTP call
          return of(undefined as void);
        }

        const authority = `https://cognito-idp.${oidcConfig.cognito_region}.amazonaws.com/${oidcConfig.user_pool_id}`;
        const wellKnownUrl = `${authority}/.well-known/openid-configuration`;

        return this.http.get<{ token_endpoint: string }>(wellKnownUrl).pipe(
          map((liveDoc) => {
            const liveHost = new URL(liveDoc.token_endpoint).host;
            const cachedHost = new URL(cachedEndpoints.tokenEndpoint!).host;

            if (liveHost !== cachedHost) {
              console.warn('[AuthCacheGuard] Stale OIDC endpoints detected — purging localStorage blob.', {
                cachedHost,
                liveHost,
                storageKey,
              });
              localStorage.removeItem(storageKey);
            }
          }),
          catchError((err) => {
            console.warn('[AuthCacheGuard] Could not validate OIDC endpoints — proceeding without cache check.', err);
            return of(undefined as void);
          }),
        );
      }),
    );
  }

  /**
   * Reads `authWellKnownEndPoints` from the OIDC state blob in localStorage.
   * Returns null if the key is absent, the blob is unparseable, or the
   * `authWellKnownEndPoints` field is missing.
   *
   * angular-auth-oidc-client v19 stores all state as a single JSON blob
   * under key `0-{clientId}`.
   */
  private readCachedEndpoints(storageKey: string): { tokenEndpoint?: string } | null {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as {
        authWellKnownEndPoints?: { tokenEndpoint?: string };
      };
      return parsed?.authWellKnownEndPoints ?? null;
    } catch {
      return null;
    }
  }
}
